import { ReactNode, useContext, useMemo } from 'react';
import { Trans } from '@lingui/react/macro';
import { useLingui } from '@lingui/react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import UWEFormContext from '../../../context/UWEFormContext';
import { AuthContext } from '../../../context/AuthContext';
import UWEEntityApi from '../../../api/UWEEntityApi';
import useResourceLoader from '../../../util/useResourceLoader';
import { isRemarkResponded } from '../../../blc/useDataRemarks';
import Loader from '../../Loader';

dayjs.extend(utc);

type EntityNote = { createdAt: string };

function Stat({ value, label, className }: { value: ReactNode; label: ReactNode; className: string }) {
  return (
    <div className="flex-1 rounded-md bg-surface-2 px-2 py-2 text-center">
      <div className={`text-xl font-bold leading-tight ${className}`}>{value}</div>
      <div className="text-ink-3 small">{label}</div>
    </div>
  );
}

function SectionLabel({ children }: { children: ReactNode }) {
  return <div className="uppercase small font-bold text-ink-3 mb-1">{children}</div>;
}

/** Counts of the report's flags (data remarks) by state, and of its notes. */
export function FlagsNotesCard() {
  const { i18n } = useLingui();
  const auth = useContext(AuthContext);
  const UweFormContext = useContext(UWEFormContext);
  const entityId = UweFormContext?.currentEntity?.id;
  const dataRemarks = UweFormContext?.dataRemarks ?? [];
  const loadingFlags = UweFormContext?.loadingDataRemarks;

  // Same buckets the remarks sidebar shows a reviewer: "responded" means the institution has the last word.
  const flagCounts = useMemo(() => {
    const counts = { active: 0, responded: 0, resolved: 0 };
    for (const remark of dataRemarks) {
      if (remark.isResolved) counts.resolved++;
      else if (isRemarkResponded(remark, auth, 'review')) counts.responded++;
      else counts.active++;
    }
    return counts;
  }, [dataRemarks, auth]);

  const [notes, loadingNotes, notesError] = useResourceLoader<EntityNote[]>(async () => {
    if (!entityId) return [];
    const wfAccessToken = await UWEEntityApi.createWfAccessTokenHash({ uweEntityId: entityId, canView: true, canCreate: false, canUpdate: false });
    return UWEEntityApi.getUWEEntityNotes({ entityId, wfAccessToken });
  }, [entityId]);

  const latestNoteAt = useMemo(() => {
    // Notes come back as UTC timestamps without an offset, as UWEEntityNote reads them.
    const times = (notes ?? []).map(note => dayjs.utc(note.createdAt).valueOf()).filter(time => !Number.isNaN(time));
    return times.length ? Math.max(...times) : null;
  }, [notes]);
  const latestNoteDate = latestNoteAt
    ? dayjs(latestNoteAt)
        .locale(i18n.locale)
        .format('ll')
    : null;

  return (
    <div className="rounded-lg border border-line bg-card p-4 gap-2">
      <div className="font-bold mb-3">
        <Trans>Flags & notes</Trans>
      </div>

      <SectionLabel>
        <Trans>Flags</Trans>
      </SectionLabel>
      {loadingFlags ? (
        <Loader dots sm inline size={14} />
      ) : (
        <div className="flex gap-2 mb-3">
          <Stat value={flagCounts.active} label={<Trans>Active</Trans>} className="text-danger" />
          <Stat value={flagCounts.responded} label={<Trans>Responded</Trans>} className="text-warning" />
          <Stat value={flagCounts.resolved} label={<Trans>Resolved</Trans>} className="text-success" />
        </div>
      )}

      <div className="mt-6">
        <SectionLabel>
          <Trans>Notes</Trans>
        </SectionLabel>
      </div>

      {loadingNotes ? (
        <Loader dots sm inline size={14} />
      ) : notesError ? (
        <span className="text-ink-3 small">
          <Trans>Notes could not be loaded.</Trans>
        </span>
      ) : (
        <div className="flex items-baseline gap-2">
          <span className="text-xl font-bold text-primary">{notes?.length ?? 0}</span>
          <span className="text-ink-3 small">{latestNoteDate ? <Trans>latest on {latestNoteDate}</Trans> : <Trans>no notes yet</Trans>}</span>
        </div>
      )}
    </div>
  );
}
